import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import { SelectedServicesSummary } from "@/components/onboarding/formular-notificare/SelectedServicesSummary";
import type { ClassificationType } from "@/types/entity";
import type { ClassificationResult } from "@/utils/nis2-classifier";
import type { ContactData } from "@/types/contact";
import type { SelectedService } from "@/types/service";
import type { ImpactAnalysisCalculatedResult } from "@/types/impact-analysis";
import {
  Building2,
  Loader2,
  Shield,
  Users,
  FileCheck,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";

type OnboardingConfirmModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isSaving: boolean;
  entityName: string;
  cui: string;
  selectedServices: SelectedService[];
  classificationResult: ClassificationResult | null;
  completionPercentage: number;
  contactData: ContactData;
  needsArticle9: boolean;
  article9Results: ImpactAnalysisCalculatedResult;
};

function formatContactName(person: { prenume: string; nume: string }): string {
  const full = `${person.prenume} ${person.nume}`.trim();
  return full || "Necompletat";
}

export function OnboardingConfirmModal({
  open,
  onOpenChange,
  onConfirm,
  isSaving,
  entityName,
  cui,
  selectedServices,
  classificationResult,
  completionPercentage,
  contactData,
  needsArticle9,
  article9Results,
}: OnboardingConfirmModalProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader className="shrink-0">
          <AlertDialogTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-ds-blue-600" />
            Confirmare trimitere date
          </AlertDialogTitle>
          <AlertDialogDescription>
            Verificați rezumatul datelor înainte de trimitere.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 flex-1 overflow-y-auto min-h-0">
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <Building2 className="w-4 h-4 text-ds-text-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-ds-text-500">Entitate</p>
                <p className="text-sm font-medium text-navy-900 truncate">
                  {entityName || "Necompletat"}
                  {cui && (
                    <span className="text-ds-text-400 font-normal">
                      {" "}
                      (CUI: <span className="font-mono">{cui}</span>)
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="w-4 h-4 text-ds-text-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-ds-text-500">Clasificare NIS2</p>
                <div className="mt-1">
                  <ClassificationBadge
                    classification={
                      (classificationResult?.classificationV2 as ClassificationType) ??
                      null
                    }
                    size="sm"
                  />
                </div>
              </div>
            </div>

            {needsArticle9 && (
              <div className="flex items-start gap-3">
                <FileText className="w-4 h-4 text-ds-text-500 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-ds-text-500 mb-1">
                    Art. 9 - Evaluare Impact
                  </p>
                  <div className="space-y-0.5">
                    <Article9Row
                      label="lit. a)"
                      value={
                        article9Results.article9a === true
                          ? "DA"
                          : article9Results.article9a === false
                            ? "NU"
                            : "—"
                      }
                    />
                    <Article9Row
                      label="lit. b)"
                      value={article9Results.article9b ?? "—"}
                    />
                    <Article9Row
                      label="lit. c)"
                      value={article9Results.article9c ?? "—"}
                    />
                    <Article9Row
                      label="lit. d)"
                      value={
                        article9Results.article9d === true
                          ? "DA"
                          : article9Results.article9d === false
                            ? "NU"
                            : "—"
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <SelectedServicesSummary selectedServices={selectedServices} />

          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-ds-text-500" />
              <p className="text-xs text-ds-text-500 font-medium">
                Persoane de contact
              </p>
            </div>
            <div className="space-y-1 text-sm">
              <ContactRow
                label="Reprezentant legal"
                name={formatContactName(contactData.reprezentantLegal)}
              />
              <ContactRow
                label="Responsabil securitate"
                name={formatContactName(contactData.responsabilSecuritate)}
              />
              <ContactRow
                label="Contact operațional"
                name={formatContactName(contactData.contactOperational)}
              />
            </div>
          </div>

          {completionPercentage < 100 && (
            <div className="flex items-start gap-2 bg-ds-amber-50 border border-ds-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-ds-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-ds-amber-800">
                Formularul nu este completat 100%. Datele lipsă pot fi
                completate ulterior.
              </p>
            </div>
          )}
        </div>

        <AlertDialogFooter className="shrink-0">
          <AlertDialogCancel disabled={isSaving}>Anulează</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Se salvează...
              </>
            ) : (
              "Confirmă și salvează"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Article9Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ds-text-500 text-xs">Art. 9 {label}</span>
      <span className="font-medium text-xs text-ds-text-600">{value}</span>
    </div>
  );
}

function ContactRow({ label, name }: { label: string; name: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ds-text-500 text-xs">{label}</span>
      <span
        className={cn(
          "font-medium text-xs",
          name === "Necompletat" ? "text-ds-text-400" : "text-ds-text-600"
        )}
      >
        {name}
      </span>
    </div>
  );
}
